import React, { useEffect, useLayoutEffect, useContext, useState, useRef } from 'react';
import _ from 'lodash';
import styled from 'styled-components/macro';

import { fontkoKR, IconVolumeOn } from '@mirinae/react-ui';
import { os } from '@mirinae/js-utils/utils/userAgent';
import GTM from '@mirinae/apis/GoogleAnalytics';

import { getFields } from '@mirinae/lessons/modules/libs/elementLib';
import PlayerContext from '@mirinae/lessons/modules/contexts/player';
import { lessonsPalette, lessonsBreakPoint } from '@mirinae/lessons/modules/defines/styles';
import Element from '@mirinae/lessons/components/lesson/elements/Element';
import SuccessOverlay from '@mirinae/lessons/components/lesson/elements/shared/SuccessOverlay';
import { playTTS } from '@mirinae/lessons/modules/libs/lessonLib';
import { LessonButton } from '@mirinae/lessons/modules/defines/styles';

const Wrapper = styled(Element)`
    position: relative;
`;

const Container = styled.div`
    display: flex;
    align-items: center;
    ${!os.isOthers && `
        flex-direction: column-reverse;
        justify-content: flex-end;
    `}
`;

const BoardInfo = styled.div`
    width: ${os.isOthers ? 412 : 'git-content'}px;
    // max-width: 400px;
    position: relative;
`;

const BoardInfoChars = styled.div`
    font-family: ${fontkoKR};
    color: ${lessonsPalette.darken[1]};
    margin-bottom: ${props => 15 * (props.fontScale || 1.0)}px;
    padding-bottom: 5px;
    border-bottom: ${props => (props.line ? `1px solid ${lessonsPalette.grey[4]}` : 'none')};
    font-size: ${props => 30 * (props.fontScale || 1.0)}px;
    font-weight: bold;
    text-align: center;
  
    min-width: 275px;
    word-wrap: break-word;
    word-break: keep-all;
  
`;

const BoardInfoCharWrong = styled.span`
    color: ${lessonsPalette.lessons[2]};
`;

const BoardInfoEnglish = styled.div`
    color: ${lessonsPalette.grey[2]};
    font-size: ${props => 16 * (props.fontScale || 1.0)}px;
    text-align: center;
    white-space: pre-wrap;
`;

const BoardTable = styled.div`
    align-self: center;
    border-spacing: 10px;
    position: relative;
    animation: ${props => (props.wiggle ? 'wiggle 1s' : '')};
    animation-delay: -0.75s;

    ${os.isOthers && `
        margin-left: 40px;
    `}

    ${!os.isOthers && `
        margin-bottom: 15px;;
    `}

    @keyframes wiggle {
        0% { transform: rotate(0deg); }
       80% { transform: rotate(0deg); }
       85% { transform: rotate(5deg); }
       95% { transform: rotate(-5deg); }
      100% { transform: rotate(0deg); }
    }
`;

const BoardRow = styled.div`
    display: flex;
    flex-direction: row;
    flex-flow: space-evenly;
`;

const Cell = styled.div`
    position: relative;
    z-index: 1;
    font-size: 22px;
    font-weight: 400;
    width: 58px;
    height: 58px;
    line-height: 58px;
    text-align: center;
    cursor: pointer;
    user-select: none;
    touch-action: none;
    border: 1px solid ${lessonsPalette.grey[4]};
    color: ${lessonsPalette.grey[4]};
    border-radius: 100%;
    margin: 7px;

    @media only screen and (max-width: ${lessonsBreakPoint.sm}) {
        width: 48px;
        height: 48px;
        line-height: 48px;
        font-size: 21px;
    }
`;

const PickedCell = styled(Cell)`
    border-color: ${lessonsPalette.darken[0]};
    color: ${lessonsPalette.grey[0]};
    background-color: ${lessonsPalette.lessons[4]};
`;

const BadCell = styled(Cell)`
    border-color: ${lessonsPalette.lessons[2]};
    color: ${lessonsPalette.grey[0]};
    background-color: ${lessonsPalette.lessons[3]};
`;

const AdjacentCell = styled(Cell)`
    color: ${lessonsPalette.primary.black};
    font-weight: 600;
`;

const HiddenCell = styled(Cell)`
    visibility: hidden;
`;

const LineSVG = styled.svg`
    position: absolute;
    top: 0;
    left: 0;
    pointer-events: none;
    path {
        fill: none;
        stroke: ${lessonsPalette.light[1]};
        stroke-width: 20px;
        stroke-linecap: round;
    };
`;

const SpeakerButton = styled(LessonButton)`
    position: absolute;
    top: -30px;
    left: calc(50% - 15px);
    width: 25px;
    height: 25px;
    // opacity: 0;
    // transition: opacity 0.6s linear .6s;
`;

const parsePath = (path) => {
    // pull the tuple value pairs out and strip all whitespace
    // all coordinate keys for the sake of comparison should follow
    // the format "x,y"
    const re = /\s?[0-9]\s?,\s?[0-9]\s?/g;
    const coordStringList = path.match(re);
    return coordStringList.map((pathItem) => {
        return pathItem.replace(/\s/g, '');
    });
};

const parseFillInTheBlanks = (fillInTheBlanks) => {
    if (fillInTheBlanks) {
        const m = fillInTheBlanks.trim().match(/([^_]*)(_+)(.*)/);
        if (m)
            return [m[1], m[2], m[3]];
    }
    return ['', '', ''];
};

const Line = ({ picks, cellRefs }) => {
    const lineGeometry = () => {
        const getCenter = (coords) => {
            const node = cellRefs.current[coords].current;
            return [node.offsetLeft + node.offsetWidth / 2, node.offsetTop + node.offsetHeight / 2];
        };

        return picks.map((coords, i) => {
            const [centerX, centerY] = getCenter(coords);
            let path = '';
            if (i === 0) {
                path += `M${centerX} ${centerY}`;
            } else {
                path += ` L${centerX} ${centerY}`;
            }
            return path;
        }).join(' ');
    };

    return (
        <LineSVG width="400" height="400">
            <path d={lineGeometry()} />
        </LineSVG>
    );
};

const Board = ({
    cellRefs,
    adjacents,
    mouseAction,
    touchAction,
    picks,
    badPick,
    rows,
    complete,
}) => {
    return (
        <BoardTable wiggle={badPick != null}>
            { rows.map((row, rowIndex) => (
                <BoardRow key={rowIndex}>
                    { _.chain(row).values().map((char, cellIndex) => {
                        const key = `${rowIndex},${cellIndex}`;

                        let CellType = Cell;
                        if (adjacents.includes(key)) {
                            CellType = AdjacentCell;
                        }
                        if (picks.includes(key)) {
                            CellType = PickedCell;
                        }
                        if (badPick === key) {
                            CellType = BadCell;
                        }
                        if (complete && !picks.includes(key)) {
                            CellType = HiddenCell;
                        }

                        return (
                            <CellType
                                data-t={`phraseboard-cell-${char}`}
                                ref={cellRefs.current[key]}
                                data-key={key}
                                key={key}
                                onMouseEnter={(e) => mouseAction(e, rowIndex, cellIndex, 'enter')}
                                onMouseLeave={(e) => mouseAction(e, rowIndex, cellIndex, 'leave')}
                                onMouseDown={(e) => mouseAction(e, rowIndex, cellIndex, 'down')}
                                onMouseMove={(e) => mouseAction(e, rowIndex, cellIndex, 'move')}
                                onMouseUp={(e) => mouseAction(e, rowIndex, cellIndex, 'up')}
                                onTouchMove={(e) => touchAction(e, rowIndex, cellIndex, 'move')}
                                onTouchEnd={(e) => touchAction(e, rowIndex, cellIndex, 'end')}
                            >
                                {char}
                            </CellType>
                        );
                    }).value()}
                </BoardRow>
            ))}
            { picks.length > 1 && <Line picks={picks} cellRefs={cellRefs} /> }
        </BoardTable>
    );
};

const PhraseBoard = ({ element }) => {
    const {
        table,
        english,
        path: rawPath,
        fillInTheBlanks,
        //answerFontScale,
    } = getFields(element, 'table', 'english', 'path', 'fillInTheBlanks'); // , 'answerFontScale');
    const path = parsePath(rawPath);
    const [prefix, blanks, postfix] = parseFillInTheBlanks(fillInTheBlanks);

    const player = useContext(PlayerContext);
    const [state, setState] = useState({
        badPick: null, 
        picks: [], 
        interactive: true,
        complete: false,
    });

    const koAnswerRef = useRef(null);
    const enAnswerRef = useRef(null);
    const [answerFontScale, setAnswerFontScale] = useState(1);


    useLayoutEffect(() => {
        const checkResize = () => {
            // adapt font-size if answer wraps
            if (koAnswerRef.current && enAnswerRef.current) {
                const koFontSize = 30;
                const enFontSize = 16;
                const scaleIfWraps = 24.0 / koFontSize; // minimum fontsize = 20px
                if (answerFontScale === 1 && (koAnswerRef.current.offsetHeight > 2 * koFontSize || enAnswerRef.current.offsetHeight > 2 * enFontSize)) { // heuristic
                    // reduce font-size if we wrap
                    setAnswerFontScale(scaleIfWraps);
                }
            }
        };
        if (koAnswerRef.current) {
            const observer = new ResizeObserver(checkResize);
            observer.observe(koAnswerRef.current);
            return () => observer.disconnect();
        }
    }, []);

    const touchedNode = useRef(null);
    const mouseDown = useRef(false);
    // use forwarded refs for SVG path calculation rather than using
    // explicit references to DOM nodes, which React is not guaranteed
    // to reuse. this creates a map of 'cell key' (coordinate string, e.g. '1,1')
    // to a ref that is picked up by the cells in the table.
    const cellRefs = useRef(_.reduce(table.rows, (acc, row, rowIndex) => {
        _.forEach(_.values(row), (_, cellIndex) => {
            acc[`${rowIndex},${cellIndex}`] = React.createRef();
        });
        return acc;
    }, {}));

    const updateState = (update) => {
        setState(prev => ({ ...prev, ...update }));
    };

    const checkForSuccess = () => {
        if (!state.complete && (state.picks.length === path.length)) {
            updateState({
                interactive: false,
                complete: true,
            });

            player.postEvent({ action: 'complete' }, element);
            GTM.userAction('success', { page: player.currentPage.componentName }, 'lessons');
        }
    };

    const addPick = (key) => {
        updateState({
            badPick: null, 
            picks: state.picks.concat(key),
        });
    };

    const badPick = (key) => {
        updateState({
            badPick: key,
        });
        GTM.userAction('error', { page: player.currentPage.componentName }, 'lessons');
        player.postEvent({ action: 'error' }, element);
   };

    const getCursorAdjacentCells = () => {
        // we want to render 'adjacent' cells slightly
        // differently to notify the user that the only
        // possible picks are cardinally adjacent to the
        // previous pick.
        if (state.picks.length === 0) {
            return [];
        }

        const cursor = state.picks[state.picks.length - 1];
        const [yString, xString] = cursor.split(',');
        const [y, x] = [parseInt(yString, 10), parseInt(xString, 10)];
        const dimension = table.rows.length; // n by n square always
        const adjacents = [];

        if (y > 0) {
            adjacents.push(`${y - 1},${x}`); // up
        }
        if (y < dimension - 1) {
            adjacents.push(`${y + 1},${x}`); // down
        }
        if (x > 0) {
            adjacents.push(`${y},${x - 1}`); // left
        }
        if (x < dimension - 1) {
            adjacents.push(`${y},${x + 1}`); // right
        }

        return adjacents;
    };

    const getPickedCharsString = () => {
        return state.picks.map((pick) => {
            const cellRef = cellRefs.current[pick].current;
            if (cellRef != null) {
                // some cells may be whitespace, which shoudl be represented
                // as a single space in the output. in these cases the innerText
                // will be an empty string.
                const text = cellRef.innerText.trim();
                return text === '' ? ' ' : text;
            }
        });
    };

    const getBlanks = () => {
        return blanks.substring(0, blanks.length - (getPickedCharsString().length + (state.badPick ? 1 : 0)));
    };

    const onlyWhenInteractive = (fn) => {
        const inner = (...args) => {
            if (state.interactive) {
                return fn(...args);
            }
        };
        return inner;
    };

    const mouseAction = onlyWhenInteractive((e, rowIndex, cellIndex, action) => {
        const key = `${rowIndex},${cellIndex}`;
        const shouldPick = path[state.picks.length] === key;

        switch (action) {
        // case 'enter':
        //     if (mouseDown.current && shouldPick && !state.pickedNodes.includes(target)) {
        //         addPick(target, key);
        //     }
        //     break;
        // see comment below
        case 'down':
            mouseDown.current = true;
            if (shouldPick) {
                addPick(key);
            } else if (!state.picks.includes(key)) {
                badPick(key);
            }
            break;
        case 'up':
            // this doesn't capture mouseup outside of the target dom element, so
            // it is VERY flaky. implement this with a global mouse event listener?
            // todo(budleigh)
            mouseDown.current = false;
            break;
        default:
            break;
        }
    });

    const touchAction = onlyWhenInteractive((e, rowIndex, cellIndex, action) => {
        const key = `${rowIndex},${cellIndex}`;

        if (action === 'move') {
            const nodeTouched = document.elementFromPoint(e.touches[0].clientX, e.touches[0].clientY);
            const shouldPick = path[state.picks.length] === key;
            if (nodeTouched) {
                if (nodeTouched !== touchedNode.current && nodeTouched.getAttribute('data-key')) {
                    touchedNode.current = nodeTouched;
                    if (shouldPick && !state.picks.includes(key)) {
                        addPick(key);
                    } else if (!state.picks.includes(key)) {
                        // this ain't working? TODO(budleigh)
                        badPick(key);
                    }
                }
            } else {
                touchedNode.current = null;
            }
        } else if (action === 'end') {
            touchedNode.current = null;
        }
    });

    checkForSuccess();
    
    const speak = (e) => {
        e.stopPropagation();
        e.preventDefault();
        if (koAnswerRef.current)
            playTTS(null, koAnswerRef.current.textContent, e);
    };

    return (
        <Wrapper element={element}>
            {state.complete ? <SuccessOverlay /> : null}
            <Container>
                <BoardInfo>
                    <BoardInfoChars ref={koAnswerRef} fontScale={answerFontScale} line={getBlanks().length === 0}>
                        {prefix}
                        {getPickedCharsString()}
                        {state.badPick && <BoardInfoCharWrong>{cellRefs.current[state.badPick].current.innerText}</BoardInfoCharWrong>}
                        {getBlanks()}
                        {postfix}
                    </BoardInfoChars>
                    <BoardInfoEnglish ref={enAnswerRef} fontScale={answerFontScale}>
                        {english}
                    </BoardInfoEnglish>
                    { state.complete && (
                        <SpeakerButton type="button" onClick={speak}>
                            <IconVolumeOn width="25" height="25" />
                        </SpeakerButton>
                    )}
                </BoardInfo>
                <Board
                    cellRefs={cellRefs}        
                    mouseAction={mouseAction}
                    touchAction={touchAction}
                    picks={state.picks}
                    adjacents={getCursorAdjacentCells()}
                    badPick={state.badPick}
                    path={path}
                    rows={table.rows}
                    complete={state.complete}
                />
            </Container>
        </Wrapper>
    );
};

export default PhraseBoard;
